import React, { KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import styled from 'styled-components';
import { Form, Input, type FormInstance } from 'antd';
import { t } from 'ttag';
import {
  FacebookFilled,
  GlobalOutlined,
  LinkedinFilled,
  PictureOutlined,
  TwitterSquareFilled,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { NamePath } from 'antd/lib/form/interface';
import { Rule } from 'antd/lib/form';

import Label from './Label';

export enum UrlTypes {
  Facebook = 'facebook',
  Image = 'image',
  LinkedIn = 'linkedin',
  Twitter = 'twitter',
  Video = 'video',
  Website = 'website',
}
const ALLOWED_PROTOCOLS = ['http', 'https'];
const RETURN_KEY_CODE = 13;

const FACEBOOK_PATTERN =
  /^https:\/\/(www\.)?([a-zA-Z0-9]([-a-zA-Z0-9]{0,61}[a-zA-Z0-9])?\.)?facebook\.com\/([^\s])+$/;
const LINKEDIN_PATTERN =
  /^https:\/\/(www\.)?([a-zA-Z0-9]([-a-zA-Z0-9]{0,61}[a-zA-Z0-9])?\.)?linkedin\.com\/([^\s])+$/;
const TWITTER_PATTERN =
  /^https:\/\/(www\.)?([a-zA-Z0-9]([-a-zA-Z0-9]{0,61}[a-zA-Z0-9])?\.)?(twitter|x)\.com\/([^\s])+$/;
const VIDEO_PATTERN =
  // eslint-disable-next-line no-useless-escape
  /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:\/?#[\]@!\$&'\(\)\*\+,;=]\/([^\s])+$/;

const Field = styled(Form.Item)`
  margin-bottom: 0;
`;

export const enforceLinkProtocol = (url, type = '') => {
  if (!url) {
    return null;
  }

  const protocol = url.match(/(?<protocol>.+):\/\//);

  // prepend https when the provided URL doesn't have one of the allowed protocols
  // its for keeping backward compatibility with previous implementation
  if (url !== '' && !ALLOWED_PROTOCOLS.includes(protocol?.groups?.protocol)) {
    return `https://${url}`;
  }

  // prepend https when the provided URL doesn't have the secure protocol to social url
  if (
    (type === 'twitter' || type === 'linkedin' || type === 'facebook') &&
    url.startsWith('http://')
  ) {
    return url.replace('http://', 'https://');
  }

  return url;
};

interface DataEntry {
  icon: ReactNode;
  sampleURL: string;
  placeholder: string;
  // These are a subset of RuleType from rc-field-form/lib/interface.d.ts
  type?: 'string' | 'url';
  pattern?: RegExp;
}

const DATA: Record<UrlTypes, DataEntry> = {
  [UrlTypes.Website]: {
    icon: <GlobalOutlined />,
    sampleURL: 'https://www.eventmobi.com',
    placeholder: 'https://www.eventmobi.com/',
    type: 'url',
  },
  [UrlTypes.Image]: {
    icon: <PictureOutlined />,
    sampleURL: 'https://www.eventmobi.com/...',
    placeholder: 'https://www.eventmobi.com/image.jpg',
    type: 'url',
  },
  [UrlTypes.Twitter]: {
    icon: <TwitterSquareFilled />,
    sampleURL: 'https://x.com/...',
    type: 'string',
    pattern: TWITTER_PATTERN,
    placeholder: 'https://x.com/EventMobi',
  },
  [UrlTypes.LinkedIn]: {
    icon: <LinkedinFilled />,
    sampleURL: 'https://linkedin.com/company/...',
    type: 'string',
    pattern: LINKEDIN_PATTERN,
    placeholder: 'https://linkedin.com/company/eventmobi',
  },
  [UrlTypes.Facebook]: {
    icon: <FacebookFilled />,
    sampleURL: 'https://facebook.com/...',
    type: 'string',
    pattern: FACEBOOK_PATTERN,
    placeholder: 'https://facebook.com/EventMobi',
  },
  [UrlTypes.Video]: {
    icon: <VideoCameraOutlined />,
    sampleURL: 'https://youtube.com/...',
    type: 'string',
    pattern: VIDEO_PATTERN,
    placeholder: 'https://www.youtube.com/watch?v=video-id',
  },
};

interface Props {
  dependencies?: NamePath[];
  disabled?: boolean;
  label?: string;
  name: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  requiredMessage?: string;
  rules?: Rule;
  setFieldValue: FormInstance['setFieldValue'];
  type: UrlTypes;
  validateFields: (fields: string[]) => Promise<any>;
  httpsOnly?: boolean;
}

const Url = ({
  dependencies = [],
  disabled = false,
  label = '',
  name,
  onKeyDown = undefined,
  placeholder = null,
  required = false,
  requiredMessage = '',
  rules = undefined,
  setFieldValue,
  type,
  validateFields,
  httpsOnly = false,
}: Props) => {
  const handleOnBlur = async (e: SyntheticEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    try {
      await validateFields([name]);
      return setFieldValue(name, enforceLinkProtocol(value, type));
    } catch {
      return null;
    }
  };

  const handleKeyPress = async (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.keyCode || e.which;

    if (key !== RETURN_KEY_CODE) {
      return;
    }

    await handleOnBlur(e);
  };

  const { icon, pattern, type: ruleType, placeholder: defaultPlaceholder, sampleURL } = DATA[type];

  const urlRule: Rule = {
    message: t`Invalid URL. Please format as follows: '${sampleURL}'`,
    transform: (url: string) => enforceLinkProtocol(url, type),
    type: ruleType,
    pattern,
  };

  return (
    <Field
      dependencies={dependencies}
      label={label ? <Label isRequired={required}>{label}</Label> : null}
      name={name}
      rules={[
        { required, message: requiredMessage ?? t`URL is required.` },
        urlRule,
        httpsOnly
          ? {
              pattern: /^https:\/\//,
              message: t`URL must start with https://`,
            }
          : {},
        rules ?? {},
      ]}
    >
      <Input
        disabled={disabled}
        placeholder={placeholder ?? defaultPlaceholder}
        prefix={icon}
        onKeyPress={handleKeyPress}
        onKeyDown={onKeyDown}
        onBlur={handleOnBlur}
      />
    </Field>
  );
};

export default Url;
